import {
  Policy,
  TechAdapterDeploymentDetails,
  TechAdapterDetails,
  UpsertPolicy,
  UpsertTechAdapterDeploymentRequest,
} from '../api';
import _ from 'lodash';

export const TECH_LABEL_PREFIX = 'technology';

/**
 * Given a TechAdapterDeployment and some options, create an update request for that TechAdapterDeployment that only changes the properties specified in options
 * @param baseDeploymentDetails The base deployment object we want to update
 * @param techAdapterId The tech adapter id
 * @param options This field allows to specifiy what we want to change about the original deployment, ensuring we don't lose properties by mistake
 * @returns {UpsertTechAdapterDeploymentRequest}
 */
export const createDeploymentUpdateRequest = (
  baseDeploymentDetails: TechAdapterDeploymentDetails,
  techAdapterId: string,
  options?: {
    fieldsOverride?: { url?: string; enabled?: boolean };
    removePolicies?: string[];
    addPolicies?: UpsertPolicy[];
  },
): UpsertTechAdapterDeploymentRequest => {
  const upsertRequest: any = _.cloneDeep(baseDeploymentDetails);

  // if the policies path is not on the deployment and we are adding policies, we create the path first
  if (options?.addPolicies) {
    if (!upsertRequest.properties) upsertRequest.properties = {};
    if (!upsertRequest.properties.dataContractGuardian)
      upsertRequest.properties.dataContractGuardian = {};
    if (!upsertRequest.properties.dataContractGuardian.policies)
      upsertRequest.properties.dataContractGuardian.policies = [];
  }

  if (upsertRequest.properties?.dataContractGuardian?.policies) {
    let policies: UpsertPolicy[] = (
      upsertRequest.properties.dataContractGuardian.policies as Policy[]
    ).map((policy: Policy) => ({
      resourceType: policy.resourceType,
      spec: { id: policy.id },
    }));

    if (options?.removePolicies) {
      const excludedSet = new Set(options?.removePolicies);
      policies = policies.filter(p => !excludedSet.has((p as any).spec.id));
    }

    if (options?.addPolicies) {
      policies.push(...options.addPolicies);
    }
    upsertRequest.properties.dataContractGuardian.policies = policies;
  }

  return {
    id: techAdapterId,
    environment: upsertRequest.environment,
    url: options?.fieldsOverride?.url ?? upsertRequest.url,
    enabled: options?.fieldsOverride?.enabled ?? upsertRequest.enabled,
    properties: upsertRequest.properties,
  } as UpsertTechAdapterDeploymentRequest;
};

export const upsertTechAdapterTechnology = (
  technologyUrn: string | undefined | null,
  currentLabelObject?: string[],
): string[] | undefined => {
  // If the current array is undefined, initialize it as an empty array
  let labels = currentLabelObject ?? [];

  // Filter out any existing "technology=" labels
  labels = labels.filter(label => !label.startsWith('technology='));

  // If the technologyUrn is not null or undefined, add the new "technology=" label
  if (technologyUrn) {
    labels.push(`technology=${technologyUrn}`);
  }

  // Return the updated array, or undefined if the array is empty
  return labels.length > 0 ? labels : undefined;
};

export const technologyIdToLabel = (id: string) => `${TECH_LABEL_PREFIX}=${id}`;

export const extractTechnologyIdFromLabel = (label: string) => {
  const segments = label.split('=');
  if (
    segments.length !== 2 ||
    segments[0] !== TECH_LABEL_PREFIX ||
    !segments[1]
  )
    return undefined;
  return segments[1];
};

export const extractTechnologyFromTechAdapter = (
  techAdapter: TechAdapterDetails,
): string | undefined => {
  // Check if the labels array exists
  if (!techAdapter.labels) {
    return undefined;
  }

  // Find the label that starts with "technology="
  const technologyLabel = techAdapter.labels.find(label =>
    label.startsWith(`${TECH_LABEL_PREFIX}=`),
  );

  // Extract and return the technology URN if the label exists
  return technologyLabel
    ? extractTechnologyIdFromLabel(technologyLabel)
    : undefined;
};
